import React, { useEffect, useRef, useState } from 'react';
import { IonIcon } from '@ionic/react';
import { chevronDown } from 'ionicons/icons';
import './su-components.css';

export interface SuSelectOption {
  value: string;
  label: string;
}

export interface SuSelectProps {
  value: string;
  options: SuSelectOption[];
  onChange: (value: string) => void;
  ariaLabel: string;
  /** compact = shrink-to-fit with a smaller font (card headers). */
  variant?: 'grow' | 'compact';
  /** Text shown when `value` matches no option (e.g. a state that is not in the list yet). */
  fallbackLabel?: string;
}

/**
 * Themed dropdown. Replaces the native <select>, which phones open as the OS picker (a different, "old" popup),
 * so the filter looks the same on a real phone as in the desktop mobile view.
 */
const SuSelect: React.FC<SuSelectProps> = ({ value, options, onChange, ariaLabel, variant = 'grow', fallbackLabel }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: Event) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = options.find((o) => o.value === value);
  return (
    <div className={`su-dd${variant === 'compact' ? ' compact' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="su-dd-btn"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="su-dd-text">{current?.label ?? fallbackLabel ?? ''}</span>
        <IonIcon icon={chevronDown} aria-hidden="true" />
      </button>
      {open && (
        <ul className="su-dd-list" role="listbox" aria-label={ariaLabel}>
          {options.map((o) => (
            <li key={o.value} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={o.value === value}
                className="su-dd-opt"
                onClick={() => {
                  setOpen(false);
                  if (o.value !== value) onChange(o.value);
                }}
              >
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SuSelect;
